import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import moment from "moment";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// API
import SupplyOrderList from "issara-sdk/apis/SupplyOrderList_apps_MSD";
import SupplyOrderEditDivision from "issara-sdk/apis/SupplyOrderEditDivision_apps_MSD";

// * Serializer (Based on API)
export type SupplyOrderListSerializer = {
  id: number;
  code: string;
  type: string;
  status: string;
  order_time: string;
  order_div: number;
  order_div_name: string;
  patient: string;
  doctor: string;
  order_perform_time: string;
  order_perform_div: number;
  order_perform_div_name: string;
  edit_user: string;
  supply_transfer_status: string;
  created: string;
  hn: string;
};

export type Props = {

}

export type State = Partial<{
  // seq
  EditDivisionSupplyOrderSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    supplyOrderList: SupplyOrderListSerializer[];
    selectedItems: SelectedItemsType[];
    searchSupplyOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type SelectedItemsType = Partial<{
  id: number;
  request_division: number | string;
  perform_division: number | string;
}>;

export type FilterSearchType = Partial<{
  created_date: string | null;
  hn: string | null;
  code: string | null;
}>;

export type PaginatedItemsType = Partial<{
  items: SupplyOrderListSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "EditDivisionSupplyOrder";
  restart?: boolean;
  clear?: boolean;
};

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

type ActionType =
  | { action: "SEARCH"; page?: number; pageSize?: number }
  | { action: "CLEAR" }
  | { action: "SAVE"; selectedItems: SelectedItemsType[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  EditDivisionSupplyOrderSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardEditDivisionSupplyOrder";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  created_date: "",
  hn: "",
  code: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

const Masters = [["division", {}]] as const;

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      EditDivisionSupplyOrderSequence: {
        ...state.EditDivisionSupplyOrderSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          page: 0,
          pageSize: LIST_LIMIT,
        },
      },
    }
    // () => Action(controller, { ...params, action: "SEARCH", page: 0, pageSize: LIST_LIMIT })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchSupplyOrder = state.EditDivisionSupplyOrderSequence?.searchSupplyOrder;
  const paginatedItems = state.EditDivisionSupplyOrderSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    ...searchSupplyOrder,
    limit: pageSize,
    offset,
  };

  const result = await GetSupplyOrderList(controller, data);

  if (result?.items) {
    controller.setState({
      EditDivisionSupplyOrderSequence: {
        ...state.EditDivisionSupplyOrderSequence,
        supplyOrderList: result?.items,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE]: "LOADING",
    },
  });

  const selectedItems = params?.selectedItems || [];

  const saveResult = await Promise.all(
    selectedItems.map((item: SelectedItemsType) =>
      SaveEditDivisionSupplyOrder(controller, {
        id: item?.id,
        request_division: item?.request_division,
        perform_division: item?.perform_division,
      })
    )
  );

  const errors = saveResult.filter((r) => r.err);
  const successes = saveResult.filter((r) => r.resp);

  if (errors.length > 0) {
    const errorDetails = errors.map((e) => `ID ${e.id}: ${e.err?.detail}`).join("\n");

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: errorDetails,
      },
    });
  } else {
    const firstSuccess = successes[0].resp;

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: firstSuccess?.detail,
      },
    });

    HandleSearchData(controller, { action: "SEARCH", page: 0, pageSize: LIST_LIMIT });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    EditDivisionSupplyOrderSequence: {
      ...state.EditDivisionSupplyOrderSequence,
      searchSupplyOrder: {
        ...FILTER_INIT,
      },
      supplyOrderList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetSupplyOrderList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await SupplyOrderList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const SaveEditDivisionSupplyOrder: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await SupplyOrderEditDivision.put({
    apiToken: controller.apiToken,
    pk: params.id,
    data: {
      ...(params?.request_division ? { request_division: params?.request_division } : {}),
      ...(params?.perform_division ? { perform_division: params?.perform_division } : {}),
    },
  });

  return {
    id: params.id,
    resp,
    err,
  };
};
